import React, { useEffect, useRef } from 'react'
import { Box } from '@mui/material'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { alpha } from '@mui/material/styles'
import { CARD_SX, DASH, ELLIPSIS, EmptyCard, FIELD_LABEL_ELLIPSIS, P, R, T, TABULAR } from './ProcessTreeInspectorParts'
// POL102: the constant-to-key map is already exported by the row. It is imported, never made again.
import { COVERAGE_STATE_KEY } from './ProcessTreeRow'

/**
 * ProcessTreeMaterialList.jsx
 * The material an operation consumes, as the process Tree inspector and the operation dialog list it:
 * exactly the lines the tree draws under the row (`resolveJobMaterialRows`), so the two can never
 * disagree. Read-only on purpose: the BOM is assigned from the Resources section, not from here.
 *
 * `loaded` false means the BOM has not arrived yet — a dash, never "no material" (RN50/POL2).
 */
const ProcessTreeMaterialList = ({ idPrefix = 'inspector', loaded = false, rows = [], selectedKey = '', onSelect = null }) => {
  const { t_ } = useSmartTranslation()
  const id = (suffix) => idPrefix + '-' + suffix
  const selectedRef = useRef(null)

  /**
   * PO review 2026-09-22: la linea marcada se trae a la vista. Se llega aqui pulsandola en el arbol, y
   * una operacion con veinte componentes la dejaria fuera de la parte visible del panel — marcada donde
   * nadie la ve. `block: 'nearest'` mueve lo justo: si ya se ve, no se mueve nada.
   * El guarda de `scrollIntoView` no es paranoia: jsdom no lo implementa.
   */
  useEffect(() => {
    const element = selectedRef.current
    if (selectedKey && element && typeof element.scrollIntoView === 'function') {
      element.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [selectedKey])

  if (!loaded) {
    return <EmptyCard testId={id('material-list')} text={DASH} />
  }
  if (rows.length === 0) {
    return <EmptyCard testId={id('material-list')} text={t_('This operation does not consume material')} />
  }
  return (
    <Box data-testid={id('material-list')} sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {rows.map((materialRow) => (
        // Story #150: keyed by the row's own key — two resources of the same job that resolve to no
        // BOM line both had an undefined workflow resource uuid and collided on one React key.
        // PO review 2026-09-22: UNA sola ventana de material. Esta lista es la que hay, y la linea que
        // se esta mirando se marca aqui en vez de abrirse en un panel aparte: filo, fondo tenue y
        // `aria-current`, los mismos recursos con los que la fila seleccionada del arbol se distingue.
        // Marcada, ademas, deja de recortar su nombre — leerlo entero es a lo que se viene.
        <Box
          key={materialRow.key}
          component={onSelect ? 'button' : 'div'}
          type={onSelect ? 'button' : undefined}
          data-testid={id('material-card-' + materialRow.key)}
          ref={materialRow.key === selectedKey ? selectedRef : null}
          data-selected={materialRow.key === selectedKey}
          aria-current={materialRow.key === selectedKey ? 'true' : undefined}
          onClick={onSelect ? () => onSelect(materialRow.key) : undefined}
          sx={{
            ...CARD_SX,
            width: '100%',
            textAlign: 'left',
            font: 'inherit',
            color: 'inherit',
            cursor: onSelect ? 'pointer' : 'default',
            transition: 'background-color 200ms ease, border-color 200ms ease, box-shadow 200ms ease',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            ...(materialRow.key === selectedKey
              ? { borderColor: P.primary.main, background: alpha(P.primary.main, 0.14), boxShadow: '0 0 0 1px ' + P.primary.main }
              : {})
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
            {/*
              * RN125/RN171: a resource with no BOM line has no verdict to show, so no badge is drawn —
              * and the verdict that IS drawn goes through the SAME constant-to-key map the tree row
              * uses. Translating the raw constant, as this did, looked up a key that exists in none of
              * the catalogues, so the panel showed the constant while the row beside it showed the
              * translation (POL102: one owner for the rule, imported, never copied).
              */}
            {materialRow.state !== null && materialRow.state !== undefined
              ? (
                <Box component='span' sx={{ flexShrink: 0, padding: '0 8px', height: '22px', borderRadius: R.card, border: '1px solid ' + T.borderSoft, color: T.accent, fontSize: '10.5px', fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                  {t_(COVERAGE_STATE_KEY[materialRow.state])}
                </Box>
                )
              : null}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/*
                * El nombre recortado pasa a leerse entero al marcarse, y eso cambia el alto de la ficha.
                * Se anima por `max-height` porque un alto automatico no transiciona: de una linea a un
                * tope holgado, que la caja nunca alcanza y por eso no deja hueco muerto debajo.
                */}
              <Box
                sx={{
                  fontSize: '14.5px',
                  fontWeight: 700,
                  color: T.textMain,
                  overflow: 'hidden',
                  maxHeight: materialRow.key === selectedKey ? '8em' : '1.35em',
                  transition: 'max-height 220ms ease',
                  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                  ...(materialRow.key === selectedKey ? { wordBreak: 'break-word' } : ELLIPSIS)
                }}
              >
                {materialRow.name || materialRow.code}
              </Box>
              <Box sx={{ fontSize: '12.5px', color: T.textFaint, ...TABULAR, ...(materialRow.key === selectedKey ? { wordBreak: 'break-word' } : {}) }}>
                {materialRow.code}{materialRow.position ? ' · ' + t_('Position') + ' ' + materialRow.position : ''}
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: '10px', marginTop: '10px', alignItems: 'flex-end' }}>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '5px' }}>
              <Box component='span' sx={FIELD_LABEL_ELLIPSIS}>{t_('Consumes')}</Box>
              <Box sx={{ height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 12px', borderRadius: R.field, border: '1px solid ' + T.borderSoft, background: T.bgCard, color: T.textFaint, fontSize: '14px', ...TABULAR }}>
                {materialRow.quantity === null ? DASH : materialRow.quantity}
              </Box>
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  )
}

export default ProcessTreeMaterialList
